// Colombia is always UTC-5 (no DST)
const COL_OFFSET_MS = 5 * 60 * 60 * 1000;

// Fecha corta (p. ej. «20 sept 2026») SIEMPRE en hora Colombia (America/Bogota), robusta a la
// zona del navegador. El bug que arregla: `new Date('2026-09-20')` (fecha-solo) se parsea como
// UTC medianoche y, mostrado en Colombia (UTC-5), retrocede al día 19. Reglas:
//  • 'YYYY-MM-DD' = fecha de CALENDARIO → se ancla al MEDIODÍA Colombia (no se corre de día).
//  • datetime con Z u offset = instante → se muestra en Bogotá.
//  • datetime SIN zona (naive, como lo serializa Woo) = hora Colombia → se le pone -05:00.
// Así una compra a las 12:02 a. m. hora Colombia se muestra en SU día, no el anterior.
export function fmtFechaCorta(value, opts = { day: '2-digit', month: 'short', year: 'numeric' }) {
  if (!value) return '—';
  const s = String(value);
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T12:00:00-05:00`
    : (/[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) ? s
      : `${s}-05:00`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-CO', { timeZone: 'America/Bogota', ...opts });
}

// Returns current Colombia date as 'YYYY-MM-DD'
export function colToday() {
  return new Date(Date.now() - COL_OFFSET_MS).toISOString().slice(0, 10);
}

// Returns Colombia date string for any Date object
export function colDateStr(date) {
  return new Date(date.getTime() - COL_OFFSET_MS).toISOString().slice(0, 10);
}

// First and last day ('YYYY-MM-DD') of a Colombia month, offset in months from the
// current one (0 = this month, -1 = previous month).
export function colMonthBounds(offsetMonths = 0) {
  const [y, m] = colToday().split('-').map(Number);
  const base = new Date(Date.UTC(y, m - 1 + offsetMonths, 1));
  const year = base.getUTCFullYear();
  const month = base.getUTCMonth();
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const mm = String(month + 1).padStart(2, '0');
  return { first: `${year}-${mm}-01`, last: `${year}-${mm}-${String(lastDay).padStart(2, '0')}` };
}

// Fecha y hora legibles en hora de Colombia ('YYYY-MM-DD HH:mm') para un
// instante (Date o string ISO con zona, como serializa Mongo). El offset vive
// UNA sola vez arriba — nunca duplicarlo en un componente (regla del proyecto).
export function colDateTimeStr(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - COL_OFFSET_MS).toISOString().slice(0, 16).replace('T', ' ');
}

// Given Colombia date strings, returns after/before params with explicit -05:00 offset
// so WooCommerce (PHP strtotime on a UTC server) correctly interprets them as Colombia time.
export function colRangeParams(afterDateStr, beforeDateStr) {
  return {
    after:  `${afterDateStr}T00:00:00-05:00`,
    before: `${beforeDateStr}T23:59:59-05:00`,
  };
}

// «Programados» (status future): el <input type="datetime-local"> da hora
// Bogotá naive ("2026-11-01T18:58"); Woo pide `date_created_gmt` en GMT. Se
// interpreta la entrada como -05:00 y se devuelve el ISO en GMT, sin milisegundos
// ni sufijo (como lo serializa Woo). Vacío/ inválido → null.
export function bogotaLocalAGmtIso(local) {
  const s = String(local || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return null;
  const base = s.length === 16 ? `${s}:00` : s.slice(0, 19);
  const d = new Date(`${base}-05:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 19); // "2026-11-01T23:58:00" (GMT)
}

// Ahora mismo, en hora Bogotá, con formato de <input datetime-local>. Default al
// activar «Programada» sin fecha previa (el usuario ajusta).
export function colAhoraInputLocal() {
  return new Date(Date.now() - COL_OFFSET_MS).toISOString().slice(0, 16);
}

// P3-12: un instante GMT de Woo (date_on_sale_*_gmt, naive UTC) → el string del
// <input datetime-local> en hora Bogotá ("2026-12-01T00:00"). Inverso de bogotaLocalAGmtIso.
export function gmtAInputBogota(gmt) {
  const s = String(gmt || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s)) return '';
  const iso = s.replace(' ', 'T').slice(0, 19);
  const d = new Date(`${iso}Z`); // GMT
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - COL_OFFSET_MS).toISOString().slice(0, 16);
}

// Una fecha/hora naive de Woo (hora Bogotá, p. ej. date_created de un programado)
// → el string del <input datetime-local> ("2026-11-01T18:58"), recortado a minutos.
export function bogotaNaiveAInputLocal(value) {
  const s = String(value || '').trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : '';
}

// «Programada para <fecha y hora Bogotá>» — la entrada es naive (hora Bogotá), se
// le pone -05:00 para formatear sin correr el día/hora.
export function fmtFechaHoraBogota(value) {
  const s = String(value || '').trim();
  if (!s) return '';
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T12:00:00-05:00`
    : /[zZ]|[+-]\d{2}:?\d{2}$/.test(s) ? s
      : `${s.replace(' ', 'T')}-05:00`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-CO', {
    timeZone: 'America/Bogota', day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}
